import { memo, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import Box from "@mui/material/Box";
import { Theme } from "@mui/material/styles";

import { Finding } from "stores/FindingStore";

import { SeverityEntry, SourceEntry, StateEntry } from "../Header/Filters/data";

import FindingRow from "./FindingRow";
import Loading from "./Loading";
import NoFindings from "./NoRecords";

// -----------------------------------------------------------------------------

const ContainerSX = {
  background: "#FFF",
  borderRight: (theme: Theme) =>
    `1px ${theme.palette.primary.border ?? ""} solid`,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  minWidth: "0vw",
  width: "min(max(256px, calc(16rem + ((1vw - 8px) * 28.75))), 440px)",

  "@media screen and (max-width: 640px)": {
    width: "100%",
  },
};

const InboxOuterContainerSX = { flex: 1, position: "relative" };

const InboxInnerContainerSX = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  right: 0,
  overflowY: "auto",
};

// -----------------------------------------------------------------------------

const OrganizationFindingsInbox = memo(function OrganizationFindingsInbox({
  findings,
  selectedFindings,
  isLoading,
  allFindingsLoaded,
  noFindings,
  loadNextPage,
  onFindingClick,
  severities,
  states,
  sources,
  searchText,
}: OrganizationFindingsInboxProps): JSX.Element {
  //
  const selectedFindingsByUuid = useMemo(
    () =>
      selectedFindings.reduce(
        (obj: { [uuid: string]: boolean }, finding: Finding) => {
          obj[finding.uuid] = true;

          return obj;
        },
        {}
      ),
    [selectedFindings]
  );

  const renderedSelectedFindingsByUuid: { [uuid: string]: boolean } = {};

  //
  return (
    <Box sx={ContainerSX}>
      <Box sx={InboxOuterContainerSX}>
        <Box sx={InboxInnerContainerSX} id="inbox-container">
          <InfiniteScroll
            hasMore={!allFindingsLoaded}
            next={loadNextPage as any}
            dataLength={findings.length}
            loader={<Loading />}
            scrollableTarget="inbox-container"
          >
            {noFindings ? <NoFindings /> : null}
            {[...findings, ...Object.values(selectedFindings)].map(
              (finding) => {
                if (
                  renderedSelectedFindingsByUuid[finding.uuid] ||
                  !severities.some(
                    (filter) =>
                      filter.all || filter.severity === finding.severity
                  ) ||
                  !states.some(
                    (filter) => filter.all || filter.state === finding.state
                  ) ||
                  !sources.some(
                    (filter) => filter.all || filter.source === finding.source
                  )
                ) {
                  return null;
                }

                if (
                  searchText &&
                  !finding.name.toLowerCase().includes(searchText.toLowerCase())
                ) {
                  return null;
                }

                if (selectedFindingsByUuid[finding.uuid]) {
                  renderedSelectedFindingsByUuid[finding.uuid] = true;
                }

                //
                return (
                  <FindingRow
                    key={finding.uuid}
                    finding={finding}
                    onClick={(remove: boolean) =>
                      onFindingClick(finding, false, remove)
                    }
                    onShiftClick={(remove: boolean) =>
                      onFindingClick(finding, true, remove)
                    }
                    selected={selectedFindingsByUuid[finding.uuid]}
                    canSelect={selectedFindings.length < 4}
                  />
                );
              }
            )}
            {isLoading ? <Loading /> : null}
          </InfiniteScroll>
        </Box>
      </Box>
    </Box>
  );
});

export default OrganizationFindingsInbox;

// -----------------------------------------------------------------------------

interface OrganizationFindingsInboxProps {
  findings: Finding[];
  isLoading: boolean;
  allFindingsLoaded: boolean;
  noFindings: boolean;
  loadNextPage: Function;
  selectedFindings: Finding[];
  onFindingClick: (
    finding: Finding,
    appendToList: boolean,
    remove: boolean
  ) => void;
  severities: SeverityEntry[];
  states: StateEntry[];
  sources: SourceEntry[];
  searchText: string;
}
